import { ApiError, request } from './client'

const BASE = import.meta.env.VITE_API_BASE ?? ''

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    let message = res.statusText
    try {
      const data = await res.json()
      if (typeof data?.message === 'string') message = data.message
    } catch { /* ignore */ }
    throw new ApiError(res.status, message)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export interface AuthUser {
  id: string
  username: string
  role: string
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  user: AuthUser
}

export interface RefreshResponse {
  access_token: string
}

export interface UserRow {
  id: string
  username: string
  role: string
  is_active: boolean
  last_login_at: string | null
  created_at: string
}

export interface AuthMode {
  auth_required: boolean
  has_users: boolean
  user?: AuthUser
}

export const authApi = {
  login: (username: string, password: string) =>
    post<LoginResponse>('/api/auth/login', { username, password }),

  refresh: (refresh_token: string) =>
    post<RefreshResponse>('/api/auth/refresh', { refresh_token }),

  logout: (refresh_token: string) =>
    post<void>('/api/auth/logout', { refresh_token }),

  /** First-run: create initial admin — only works when DB has no users. */
  bootstrap: (username: string, password: string) =>
    post<LoginResponse>('/api/auth/bootstrap', { username, password }),

  /** Returns auth_required + has_users so the login page can show first-run UI. */
  getMode: () =>
    request<AuthMode>('/api/auth/mode'),

  // Admin: user management
  listUsers: () =>
    request<UserRow[]>('/api/auth/users'),

  registerUser: (username: string, password: string, role: string) =>
    request<{ ok: boolean; id: string; username: string; role: string }>(
      '/api/auth/register',
      { method: 'POST', body: JSON.stringify({ username, password, role }) },
    ),

  deactivateUser: (userId: string) =>
    request<{ ok: boolean }>(`/api/auth/users/${userId}`, { method: 'DELETE' }),
}
